import React from 'react';
import { DateRange } from '@mui/x-date-pickers-pro';
import { addMonths, endOfDay, startOfDay } from 'date-fns';

type Params = {
  value: DateRange<Date>;
  setValue: (value: DateRange<Date>) => void;
  storageKey?: string;
  maxDateRangeMonths?: number | null;
};

// Buffers a DateRange behind a "draft" state — picker mutations don't reach
// the consumer until `apply()` is called. Clamps to start/end-of-day on apply
// and persists the committed value to sessionStorage when storageKey is set.
export default function useDraftDateRange({
  value,
  setValue,
  storageKey,
  maxDateRangeMonths,
}: Params) {
  const [draft, setDraft] = React.useState<DateRange<Date>>(value);

  const hasCap = !!maxDateRangeMonths && maxDateRangeMonths > 0;

  const isWithinCap = React.useMemo(() => {
    if (!hasCap || !draft[0] || !draft[1]) return true;
    return draft[1] <= addMonths(draft[0], maxDateRangeMonths!);
  }, [draft, hasCap, maxDateRangeMonths]);

  const reset = React.useCallback(() => setDraft(value), [value]);

  const clear = React.useCallback(() => setDraft([null, null]), []);

  const apply = React.useCallback(() => {
    let [start, end] = draft;
    if (start) start = startOfDay(start);
    if (end) end = endOfDay(end);
    setValue([start, end]);
    if (storageKey) {
      if (start && end) {
        sessionStorage.setItem(storageKey, JSON.stringify([start, end]));
      } else {
        sessionStorage.removeItem(storageKey);
      }
    }
  }, [draft, setValue, storageKey]);

  return {
    draft,
    setDraft,
    hasCap,
    isWithinCap,
    maxDateRangeMonths,
    reset,
    clear,
    apply,
  };
}
